import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { assets } from '../../../assets';
import { Paths } from '../../../navigation/paths';
import './custom-menu.css';
import MessageBadge from './components/message-badge';
import {useIntl} from "react-intl";

export type MenuItem1 = {
  label: string;
  path?: string;
  icon: string;
  activeIcon: string;
  children?: MenuItem2[];
  subRoutes: string[];
  badge?: JSX.Element | null;
};

export type MenuItem2 = {
  label: string;
  path: string;
  subRoutes: string[];
};

const ImageContainer = styled.div`
  position: relative;
  margin-left: 38px;
  width: 16px;
  height: 16px;
`;

const MenuIcon = styled.img.attrs(() => ({}))<{ isActive: boolean }>`
  width: 16px;
  height: 16px;
  -webkit-filter: ${(props) =>
    props.isActive ? 'drop-shadow(0px 0px 10px var(--primary-color))' : 'initial'};
  filter: ${(props) =>
    props.isActive ? 'drop-shadow(0px 0px 10px var(--primary-color))' : 'initial'};
`;

const Item1 = (props: { data: MenuItem1 }): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const location = useLocation();
  const history = useHistory();
  const isActive =
    location.pathname === props.data.path ||
    (!!props.data.children &&
      props.data.children.some(
        (c) =>
          c.path === location.pathname || c.subRoutes.some((r) => location.pathname.includes(r)),
      ));

  const handleItemClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.stopPropagation();
    event.preventDefault();
    if (props.data.children) {
      setIsOpen(!isOpen);
    } else if (props.data.path) {
      history.push(props.data.path);
    }
  };

  return (
    <>
      <div className="custom-menuitem-1" onClick={handleItemClick}>
        <div className={isActive ? 'active' : undefined} />
        <ImageContainer>
          <MenuIcon
            src={isActive ? props.data.activeIcon : props.data.icon}
            width={16}
            isActive={isActive}
          />
          {props.data.badge}
        </ImageContainer>
        <span>{props.data.label}</span>
        {props.data.children ? (
          <img
            src={assets.icons.triangle}
            className={'menu-triangle' + (isOpen ? ' up' : ' down')}
            alt="menu triangle"
          />
        ) : null}
      </div>
      <div className={'custom-menuitem-1-children-container' + (isOpen ? ' open' : '')}>
        {props.data.children
          ? props.data.children.map((c: MenuItem2, index: number) => <Item2 key={index} data={c} />)
          : null}
      </div>
    </>
  );
};

export const Item2 = (props: { data: MenuItem2 }): JSX.Element => {
  const location = useLocation();
  const history = useHistory();
  const isActive =
    location.pathname === props.data.path ||
    props.data.subRoutes.some((r) => location.pathname.includes(r));
  const handleItemClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.stopPropagation();
    event.preventDefault();
    history.push(props.data.path);
  };

  return (
    <div className="custom-menuitem-2" onClick={handleItemClick}>
      <span className={isActive ? 'active' : ''}>{props.data.label}</span>
    </div>
  );
};

const CustomMenu = (): JSX.Element => {
  const intl = useIntl();
  const data: MenuItem1[] = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ id: 'navbar.dashboard' }),
        path: Paths.dashboard,
        icon: assets.icons.menu_dashboard,
        activeIcon: assets.icons.menu_dashboard_active,
        subRoutes: [],
      },
      {
        label: intl.formatMessage({ id: 'navbar.orders' }),
        icon: assets.icons.menu_exchange_orders,
        activeIcon: assets.icons.menu_exchange_orders_active,
        subRoutes: [],
        children: [
          {
            label: intl.formatMessage({ id: 'navbar.orders_exchange_orders_' }),
            path: Paths.exchange_orders,
            subRoutes: [Paths.exchange_orders, Paths.new_exchange_order],
          },
          {
            label: intl.formatMessage({ id: 'navbar.orders_unit' }),
            path: Paths.unit_orders,
            subRoutes: [Paths.unit_orders],
          },
        ],
      },
      {
        label: intl.formatMessage({ id: 'navbar.disposals' }),
        icon: assets.icons.menu_disposals,
        activeIcon: assets.icons.menu_disposals_active,
        subRoutes: [],
        children: [
          {
            label: intl.formatMessage({ id: 'navbar.disposals_transitions' }),
            path: Paths.money_transitions,
            subRoutes: [Paths.money_transitions],
          },
          {
            label: intl.formatMessage({ id: 'navbar.disposals_security_transitions' }),
            path: Paths.security_transitions,
            subRoutes: [Paths.security_transitions],
          },
          {
            label: intl.formatMessage({ id: 'navbar.disposals_currency_conversions' }),
            path: Paths.currency_conversions,
            subRoutes: [Paths.currency_conversions],
          },
          {
            label: intl.formatMessage({ id: 'navbar.disposals_security_transfers' }),
            path: Paths.security_transfers,
            subRoutes: [Paths.security_transfers],
          },
        ],
      },
      {
        label: intl.formatMessage({ id: 'navbar.money_transfers' }),
        path: Paths.money_transfers,
        icon: assets.icons.menu_transfers,
        activeIcon: assets.icons.menu_transfers_active,
        subRoutes: [Paths.money_transfers],
      },
      {
        label: intl.formatMessage({ id: 'navbar.messages' }),
        path: Paths.messages,
        icon: assets.icons.menu_messages,
        activeIcon: assets.icons.menu_messages_active,
        subRoutes: [],
        badge: <MessageBadge />,
      },
    ];
  }, [intl]);

  return (
    <div style={{ width: '100%' }}>
      {data.map((d: MenuItem1, index: number) => (
        <Item1 key={index} data={d} />
      ))}
    </div>
  );
};

export default CustomMenu;
